import React, { useState } from "react";
import { motion } from "framer-motion";
import { TextField } from "@mui/material";
import Navbar from "./Navbar.js";
import dog1 from "../images/dogcontact1.avif";
import dog2 from "../images/dogcontact2.avif";
import Footer from "./Footer.js";
import emailjs from "@emailjs/browser";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_CONTACT_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_CONTACT_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

console.log("KEY:", EMAILJS_PUBLIC_KEY);
console.log("SERVICE:", EMAILJS_SERVICE_ID);
console.log("TEMPLATE:", EMAILJS_CONTACT_TEMPLATE_ID);

const textVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 1 } },
};

const buttonVariants = {
  hover: { backgroundColor: "#BDA697", transition: { duration: 0.3 } },
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "1rem",
    backgroundColor: "#fdf8f5",
    "& fieldset": { borderColor: "#e8ddd4" },
    "&:hover fieldset": { borderColor: "#bda697" },
    "&.Mui-focused fieldset": { borderColor: "#967d6c" },
  },
};

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, email, message } = formData;
    if (!name || !email || !message) {
      setStatus("error");
      return;
    }
    setStatus("sending");
    try {
      const result = await emailjs.send(
  EMAILJS_SERVICE_ID,
  EMAILJS_CONTACT_TEMPLATE_ID,
  {
    name: formData.name,        // was from_name
    email: formData.email,      // was from_email
    subject: formData.subject || "(no subject)",
    message: formData.message,
    reply_to: formData.email,
    year: new Date().getFullYear(),
  },
  EMAILJS_PUBLIC_KEY,
);
      if (result.status === 200) {
        setStatus("success");
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        setStatus("error");
      }
    } catch (err) {
      console.error("EmailJS error:", err);
      setStatus("error");
    }
  };

  return (
    <div>
      <Helmet>
        <title>Contact Us – Furbabies.AI</title>
        <meta
          name="description"
          content="Have questions about Furbabies.AI? We'd love to hear from you. Reach out to our team anytime."
        />
      </Helmet>
      <Navbar />

      {/* Hero */}
      <div className="bg-[#bda697] mt-44 flex flex-col md:flex-row items-center">
        <motion.div
          className="md:w-1/2 text-left ml-4 mt-2"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          <motion.h1
            variants={textVariants}
            initial="hidden"
            animate="visible"
            className="lg:text-[90px] lg:leading-[100px] text-6xl xl:pl-12 font-bold text-white"
          >
            Contact Furbabies.AI!
          </motion.h1>
        </motion.div>

        <motion.div
          className="md:w-1/2 flex justify-center mt-8 md:mt-0"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2 }}
        >
          <div className="hidden xl:block relative top-4 left-36 transform -translate-x-1/2">
            <div className="text-6xl text-[#d1c0b5] animate-paw-right-left">
              <i className="fa fa-paw"></i>
            </div>
          </div>
          <div className="hidden xl:block relative top-52 transform translate-x-1/2">
            <div className="text-4xl text-[#d1c0b5] animate-paw-left-right">
              <i className="fa fa-paw"></i>
            </div>
          </div>
          <img
            src={dog1}
            alt="Dog"
            className="rounded-xl w-full max-w-sm md:max-w-md object-cover"
            loading="lazy"
          />
        </motion.div>
      </div>

      {/* Contact Section */}
      <div className="bg-[#fdf8f5] py-20 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-start">

          {/* Left — info panel */}
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest text-[#bda697] mb-2">
                We'd love to hear from you
              </p>
              <h2 className="text-5xl lg:text-[56px] lg:leading-[64px] font-bold text-gray-800">
                Get in Touch 🐾
              </h2>
              <p className="mt-4 text-lg text-gray-500 leading-relaxed poppins-text">
                Have a question, feedback, or just want to say hi? Fill out the
                form and our team will get back to you as soon as possible.
              </p>
            </div>

            {/* Contact details */}
            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[#ede0d4] flex items-center justify-center text-[#967d6c] text-lg flex-shrink-0">
                  ✉️
                </div>
                <div>
                  <p className="font-semibold text-gray-700">Email us</p>
                  
                    <a href="mailto:social@furbabies.ai"
                    className="text-[#967d6c] hover:underline poppins-text text-sm"
                  >
                    social@furbabies.ai
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[#ede0d4] flex items-center justify-center text-[#967d6c] text-lg flex-shrink-0">
                  ⏱️
                </div>
                <div>
                  <p className="font-semibold text-gray-700">Response time</p>
                  <p className="text-gray-500 poppins-text text-sm">
                    We typically respond within 24 hours
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[#ede0d4] flex items-center justify-center text-[#967d6c] text-lg flex-shrink-0">
                  🐾
                </div>
                <div>
                  <p className="font-semibold text-gray-700">For your furbaby</p>
                  <p className="text-gray-500 poppins-text text-sm">
                    Every question matters — just like every pet does
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right — form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <div className="bg-white rounded-2xl shadow-sm border border-[#ede0d4] p-8">
              {/* Status banners */}
              {status === "success" && (
                <motion.div
                  className="text-sm font-medium rounded-xl p-4 mb-6 bg-green-50 text-green-700 border border-green-200"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  🎉 Your message has been sent! We'll be in touch soon.
                </motion.div>
              )}
              {status === "error" && (
                <motion.div
                  className="text-sm font-medium rounded-xl p-4 mb-6 bg-red-50 text-red-600 border border-red-200"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  Please fill in all required fields or try again later.
                </motion.div>
              )}
              {status === "sending" && (
                <motion.div
                  className="text-sm font-medium rounded-xl p-4 mb-6 bg-[#fdf5ef] text-[#967d6c] border border-[#e8d5c4]"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  Sending your message...
                </motion.div>
              )}

              <form className="space-y-4 poppins-text" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TextField
                    fullWidth
                    placeholder="Your name *"
                    variant="outlined"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    name="name"
                    sx={fieldSx}
                  />
                  <TextField
                    fullWidth
                    placeholder="Your email *"
                    variant="outlined"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    name="email"
                    type="email"
                    sx={fieldSx}
                  />
                </div>
                <TextField
                  fullWidth
                  placeholder="Subject (optional)"
                  variant="outlined"
                  value={formData.subject}
                  onChange={handleChange}
                  name="subject"
                  sx={fieldSx}
                />
                <TextField
                  fullWidth
                  placeholder="Your message *"
                  variant="outlined"
                  multiline
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  name="message"
                  sx={fieldSx}
                />
                <motion.button
                  variants={buttonVariants}
                  whileHover="hover"
                  disabled={status === "sending"}
                  className="mt-2 text-lg px-6 py-3 bg-[#967d6c] text-white rounded-xl w-full font-semibold disabled:opacity-60 transition-all"
                >
                  {status === "sending" ? "Sending..." : "Send Message 🐾"}
                </motion.button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>

      {/* CTA section */}
      <div className="bg-white mt-20 text-center flex flex-col-reverse md:flex-row items-center max-w-6xl mx-auto">
        <motion.div
          className="md:w-1/2 flex justify-center md:mb-0 -ml-16"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          <div className="hidden xl:block relative top-16 left-14 transform -translate-x-1/2">
            <div className="text-6xl text-[#bda697] animate-paw-left-right">
              <i className="fa fa-paw"></i>
            </div>
          </div>
          <img
            src={dog2}
            alt="Dog"
            className="rounded-xl max-w-sm md:max-w-md"
            loading="lazy"
          />
          <div className="hidden xl:block relative transform translate-x-1/2">
            <div className="text-6xl text-[#bda697] animate-paw-right-left">
              <i className="fa fa-paw"></i>
            </div>
          </div>
        </motion.div>
        <motion.div
          className="md:w-1/2 ml-4 sm:ml-8 md:ml-20 text-left px-4"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          <motion.h2
            className="text-5xl lg:text-[60px] lg:leading-[70px] font-semibold text-gray-800"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            Ready to Experience Furbabies.AI Exceptional Care?
          </motion.h2>
          <motion.p
            variants={textVariants}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="mt-4 text-xl lg:text-[18px] lg:leading-[28px] poppins-text text-gray-600"
          >
            Join Us Today and Discover a World of Happiness and Health for Your Pet.
          </motion.p>
          <motion.button
            variants={buttonVariants}
            whileHover="hover"
            className="mt-6 text-xl lg:text-[20px] lg:leading-[26px] px-12 py-4 bg-[#967d6c] text-white font-bold rounded-full"
          >
            <Link to="/signup" className="text-white">
              Join Today
            </Link>
          </motion.button>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default ContactUs;